import React, { useState, useEffect } from 'react'
import { Menu, PanelRight } from 'lucide-react'
import { VideoPlayer } from './components/VideoPlayer'
import { Timeline } from './components/Timeline'
import { SegmentList } from './components/SegmentListEnhanced'
import { TranscriptPanel } from './components/TranscriptPanel'
import { Toolbar } from './components/Toolbar'
import { ImportVideo } from './components/ImportVideo'
import { PlanUpgradePrompt, type StudioPromo } from './components/PlanUpgradePrompt'
import { StudioSettings } from './components/StudioSettings'
import { IntroOutroManager } from './components/IntroOutroManager'
import { ChatPanel } from './components/ChatPanel'
import { MusicPanel } from './components/MusicPanel'
import { SfxPanel } from './components/SfxPanel'
import { LibraryPanel } from './components/LibraryPanel'
import DesktopPromo from './components/DesktopPromo'
import { ImagePanel } from './components/ImagePanel'
import { VoicePanel } from './components/VoicePanel'
import { SampleSelector } from './components/SampleSelector'
import { YouTubePublishPanel } from './components/YouTubePublishPanel'
import { YouTubeChannelPicker } from './components/YouTubeChannelPicker'
import { ExportPanel } from './components/ExportPanel'
import { AudioMixer } from './components/AudioMixer'
import { ProjectLibrary } from './components/ProjectLibrary'
import type { CaptionStyle } from './captionStyles'
import { api, projectApi } from './api/client'
import { useProjectAutoSave } from './hooks/useProjectAutoSave'
import { loadSampleData } from './api/samples'
import type { SampleData } from './api/samples'
import { HighlightsPanel } from './components/HighlightsPanel'
import { DemoBanner, DemoGate, DemoExportModal } from './components/DemoParts'
import { IS_DEMO, DEMO_SAMPLE_ID, DEMO_HOME_URL } from './demo'
import type { Video, Segment, Transcript, IntroOutro, TranscriptSentence, ChatOp, MusicTrack } from './types'

/** True when the Studio is rendered inside a host page's iframe.
 *
 * legit2clip embeds this SPA under its own nav bar (studio_editor.html reserves
 * 56px for it), so anything nav-shaped we draw here is a SECOND menu — and our
 * "Workspace" link points at legit2cloud's /app, which is the wrong product for
 * that user entirely. When a host frames us, the host owns navigation.
 *
 * Standalone (legit2cloud's SSO entry) is unchanged: window.self === window.top,
 * so the escape link still renders and is still the only way out.
 */
const STUDIO_EMBEDDED = typeof window !== 'undefined' && window.self !== window.top


function App() {
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null)
  const [segments, setSegments] = useState<Segment[]>([])
  const [selectedSegments, setSelectedSegments] = useState<string[]>([])
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  // Caption engine/style for the burn — surfaced in the timeline header, consumed by Export.
  const [captionStyle, setCaptionStyle] = useState<CaptionStyle>('dynamic_viral')
  const [karaokeMode, setKaraokeMode] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [processingMessage, setProcessingMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  
  // Transcript state
  const [transcript, setTranscript] = useState<Transcript | null>(null)
  const [selectedSentences, setSelectedSentences] = useState<string[]>([])
  const [isTranscribing, setIsTranscribing] = useState(false)
  
  // Intro/Outro state
  const [selectedIntro, setSelectedIntro] = useState<IntroOutro | undefined>(undefined)
  const [selectedOutro, setSelectedOutro] = useState<IntroOutro | undefined>(undefined)
  const [showIntroOutro, setShowIntroOutro] = useState(false)
  
  // Active panel state
  const [activeRightPanel, setActiveRightPanel] = useState<'segments' | 'transcript' | 'intro-outro' | 'chat' | 'music' | 'sfx' | 'voice'>('chat')

  // Music bed state (from MusicPanel — passed to export as `music` when set)
  const [musicTrack, setMusicTrack] = useState<MusicTrack | null>(null)
  
  // Pipeline state (from analyze response, used by export)
  const [analyzeJobId, setAnalyzeJobId] = useState<string | null>(null)
  const [pipelineVideoUrl, setPipelineVideoUrl] = useState<string | null>(null)

  // YouTube publish state
  const [showYouTubePublish, setShowYouTubePublish] = useState(false)
  const [exportedBlob, setExportedBlob] = useState<Blob | null>(null)
  const [exportedDownloadUrl, setExportedDownloadUrl] = useState<string | null>(null)
  const [showChannelPicker, setShowChannelPicker] = useState(false)

  // Export panel state
  const [showExportPanel, setShowExportPanel] = useState(false)

  // Project library state
  const [showProjectLibrary, setShowProjectLibrary] = useState(false)

  // ── Reskin UI state ──
  // The demo's source is a landscape talk; in a 9:16 frame it would play as a thin letterboxed strip.
  const [aspect, setAspect] = useState<'9:16' | '1:1' | '16:9'>(IS_DEMO ? '16:9' : '9:16')
  const [activeCreate, setActiveCreate] = useState<null | 'video' | 'image' | 'music' | 'voice' | 'sfx' | 'library'>(null)
  // Highlights first: the picks are what the analysis is for, and a person should see them before
  // anything else. (Was 'assistant'.)
  const [rightTab, setRightTab] = useState<'highlights' | 'transcript' | 'captions' | 'assistant'>('highlights')
  // "Play clip" on a highlight plays just that moment: playback stops at the clip's end.
  const [playUntil, setPlayUntil] = useState<number | null>(null)
  // The public demo's stand-in for the export flow.
  const [showDemoExport, setShowDemoExport] = useState(false)
  // Narrow screens only. The two side rails are permanent columns from `md` up and
  // slide-over drawers below it — a phone cannot show three columns at once, and
  // shrinking them just produces three unusable ones.
  const [mobileDrawer, setMobileDrawer] = useState<'none' | 'create' | 'panels'>('none')
  const [commandText, setCommandText] = useState('')
  const [chatSeed, setChatSeed] = useState<string | null>(null)
  const [voiceClips, setVoiceClips] = useState<{ id: string; url: string; text: string; start: number; duration: number; gain_db?: number; offset?: number }[]>([])
  const [musicClips, setMusicClips] = useState<{ id: string; url: string; text: string; start: number; duration: number; gain_db?: number; offset?: number }[]>([])
  const [sfxClips, setSfxClips] = useState<{ id: string; url: string; text: string; start: number; duration: number; gain_db?: number; offset?: number }[]>([])
  const [imageClips, setImageClips] = useState<{ id: string; url: string; text: string; start: number; duration: number; gain_db?: number; offset?: number }[]>([])
  const addImageClip = (clip: { url: string; text: string; duration?: number }, start?: number) => {
    setImageClips(prev => {
      const st = typeof start === 'number' ? start : (prev.length ? Math.max(...prev.map(v => v.start + (v.duration || 0))) : 0)
      return [...prev, { id: `img-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, url: clip.url, text: clip.text, start: st, duration: clip.duration && clip.duration > 0 ? clip.duration : 3 }]
    })
  }
  const [selectedAudioClip, setSelectedAudioClip] = useState<{ kind: 'voice' | 'music' | 'sfx' | 'image'; id: string } | null>(null)
  const _clipSetters: any = { voice: setVoiceClips, music: setMusicClips, sfx: setSfxClips, image: setImageClips }
  const updateClip = (kind: string, id: string, updates: any) => {
    const setter = _clipSetters[kind]
    if (setter) setter((prev: any[]) => prev.map(c => c.id === id ? { ...c, ...updates } : c))
  }
  const removeClip = (kind: string, id: string) => {
    const setter = _clipSetters[kind]
    if (setter) setter((prev: any[]) => prev.filter(c => c.id !== id))
    setSelectedAudioClip(null)
  }
  const addVoiceClip = (clip: { url: string; text: string; duration?: number }, start?: number) => {
    const words = (clip.text || '').trim().split(/\s+/).filter(Boolean).length
    const dur = clip.duration && isFinite(clip.duration) && clip.duration > 0 ? clip.duration : Math.max(2, Math.round(words / 2.5))
    setVoiceClips(prev => {
      const s2 = typeof start === 'number' ? start : (prev.length ? Math.max(...prev.map(v => v.start + (v.duration || 0))) : 0)
      return [...prev, { id: `voice-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, url: clip.url, text: clip.text, start: s2, duration: dur, gain_db: 0, offset: 0 }]
    })
  }
  const _mkClip = (prefix: string, clip: { url: string; text: string; duration?: number }, existing: { start: number; duration: number }[], start?: number, gain = 0) => {
    const words = (clip.text || '').trim().split(/\s+/).filter(Boolean).length
    const d = clip.duration && isFinite(clip.duration) && clip.duration > 0 ? clip.duration : Math.max(2, Math.round(words / 2.5))
    const st = typeof start === 'number' ? start : (existing.length ? Math.max(...existing.map(v => v.start + (v.duration || 0))) : 0)
    return { id: `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, url: clip.url, text: clip.text, start: st, duration: d, gain_db: gain, offset: 0 }
  }
  const addMusicClip = (clip: { url: string; text: string; duration?: number }, start?: number) => {
    setMusicClips(prev => [...prev, _mkClip('music', clip, prev, start, -14)])
    setMusicTrack({ title: clip.text, filename: 'track', stream_path: clip.url, duration: clip.duration })
  }
  const addSfxClip = (clip: { url: string; text: string; duration?: number }, start?: number) => {
    setSfxClips(prev => [...prev, _mkClip('sfx', clip, prev, start, -6)])
  }

  // Plan check state
  const [hasStudioAccess, setHasStudioAccess] = useState<boolean | null>(null)
  const [userPlan, setUserPlan] = useState<string>('free')
  const [isCheckingPlan, setIsCheckingPlan] = useState(true)
  const [studioPromo, setStudioPromo] = useState<StudioPromo | null>(null)

  // Stop at the end of a highlight that was started with "Play clip".
  useEffect(() => {
    if (playUntil !== null && currentTime >= playUntil) {
      setIsPlaying(false)
      setPlayUntil(null)
    }
  }, [currentTime, playUntil])

  // Check user's plan on mount + handle YouTube OAuth callback + handle clipper job_id
  useEffect(() => {
    if (IS_DEMO) {
      // No session in the demo: open the sample straight away, with every highlight queued for
      // export so the Export button says what it would make.
      setHasStudioAccess(true)
      setUserPlan('demo')
      setIsCheckingPlan(false)
      // Set state directly: handleSampleLoad is declared below the early returns, so this
      // first-render closure cannot reach it (TDZ).
      loadSampleData(DEMO_SAMPLE_ID)
        .then((data) => {
          setCurrentVideo(data.video)
          setTranscript(data.transcript)
          setSegments(data.segments)
          setSelectedSegments(data.segments.map((s) => s.id))
        })
        .catch((e) => setErrorMessage(`The demo could not load: ${e.message}`))
      return
    }
    checkStudioAccess()
    
    // Check for YouTube OAuth redirect params
    const params = new URLSearchParams(window.location.search)
    const ytConnected = params.get('yt_connected')
    const ytError = params.get('yt_error')
    const clipperJobId = params.get('job_id')
    const savedProjectId = params.get('project')
    if (ytConnected) {
      // Clean URL and show publish panel
      window.history.replaceState({}, '', window.location.pathname)
      setShowYouTubePublish(true)
    } else if (ytError) {
      window.history.replaceState({}, '', window.location.pathname)
      alert(`YouTube connection failed: ${ytError}`)
    } else if (clipperJobId) {
      // Auto-load a completed Clipper job into Studio
      window.history.replaceState({}, '', window.location.pathname)
      loadClipperJob(clipperJobId)
    } else if (savedProjectId) {
      // Load a saved project from the library
      window.history.replaceState({}, '', window.location.pathname)
      loadSavedProject(savedProjectId)
    }
  }, [])

  // ── Auto-save project state (must be before conditional returns) ─
  const { projectId } = useProjectAutoSave({
    video: currentVideo,
    segments,
    selectedSegments,
    analyzeJobId,
    pipelineVideoUrl,
    enabled: !IS_DEMO && !!currentVideo && segments.length > 0,
  })

  const checkStudioAccess = async () => {
    setIsCheckingPlan(true)
    try {
      const response = await api.get('/user/plan')
      const data = response.data

      // Support both shapes:
      // - frontend-web: { plan: "pro" }
      // - backend-api (legacy): { plan: { tier: "pro", name: "PRO", ... } }
      const planValue =
        typeof data.plan === 'string'
          ? data.plan
          : data.plan?.tier || data.plan?.plan || data.plan?.name || 'free'

      setUserPlan(planValue || 'free')

      // frontend-web answers with the same check its server gate uses (plan, Pro on the
      // unified account, or the Discord launch promo). Older backends only send the plan.
      const planLower = String(planValue || '').toLowerCase()
      const hasAccess = typeof data.has_studio_access === 'boolean'
        ? data.has_studio_access
        : ['pro', 'business', 'admin', 'owner', 'trial_pro', 'pro_trial', 'trial'].some(
            p => planLower.includes(p) || planLower === p
          )
      setHasStudioAccess(hasAccess)
      setStudioPromo(data.studio_promo || null)
      
      if (!hasAccess) {
        console.log('Studio access denied. User plan:', planValue)
      }
    } catch (error) {
      console.error('Failed to check plan access:', error)
      setHasStudioAccess(false)
      setUserPlan('free')
    } finally {
      setIsCheckingPlan(false)
    }
  }

  // Load a completed Clipper job directly into Studio (from "Open in Studio" button)
  const loadClipperJob = async (jobId: string) => {
    setIsLoading(true)
    setProcessingMessage('Loading clips from Clipper job...')
    setErrorMessage('')
    try {
      const response = await api.get(`/studio/load-clipper-job/${jobId}`)
      const data = response.data

      if (!data.success) {
        throw new Error(data.error || 'Failed to load job')
      }

      // Set video
      const video: Video = {
        id: jobId,
        url: data.video?.url || '',
        title: data.video?.title || 'Clipper Job',
        duration: data.video?.duration || 0,
        platform: 'clipper',
      }
      setCurrentVideo(video)

      // Wire up export state so Export Clips can find the video
      setAnalyzeJobId(data.analyze_job_id || jobId)
      setPipelineVideoUrl(data.video?.url || '')

      // Set transcript if available
      if (data.transcript?.sentences?.length > 0) {
        setTranscript({
          video_id: jobId,
          language: data.transcript.language || 'en',
          words: data.transcript.words || [],
          sentences: data.transcript.sentences || [],
          duration: data.transcript.duration || data.video?.duration || 0,
          created_at: new Date().toISOString(),
        })
      }

      // Set segments (clips/highlights)
      if (data.segments?.length > 0) {
        setSegments(data.segments)
        setProcessingMessage(`Loaded ${data.segments.length} clips from Clipper!`)
      } else {
        setProcessingMessage('Job loaded — use Chat to find highlights.')
      }
    } catch (error: any) {
      const errMsg = error?.response?.data?.error || error?.message || 'Unknown error'
      console.error('Failed to load Clipper job:', errMsg)
      setErrorMessage(`Failed to load Clipper job: ${errMsg}`)
    } finally {
      setIsLoading(false)
      setProcessingMessage('')
    }
  }

  // Show loading while checking plan
  if (isCheckingPlan) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Checking access...</p>
        </div>
      </div>
    )
  }

  // Show upgrade prompt if user doesn't have access
  if (hasStudioAccess === false) {
    return <PlanUpgradePrompt promo={studioPromo} />
  }

  // Handle video import
  const handleVideoImport = async (videoData: { url: string; title: string; duration: number; thumbnail?: string; platform?: string; cloudSource?: any }) => {
    setIsLoading(true)
    setProcessingMessage('Importing video...')
    try {
      // Check if this is a pre-cached MinIO video
      const isMinioVideo = videoData.url.startsWith('minio://')
      // Check if this is a cloud storage import
      const isCloudVideo = videoData.url.startsWith('cloud://')
      // Check if this is a direct file upload (already saved by Studio upload endpoint)
      const isUploadedVideo = videoData.url.startsWith('file://') || videoData.url.includes('/uploads/') || videoData.url.startsWith('upload://')
      
      if (isMinioVideo) {
        // For MinIO cached videos, we already have all the metadata
        const video: Video = {
          id: Date.now().toString(),
          url: videoData.url,
          title: videoData.title,
          duration: videoData.duration,
          thumbnail: '', // MinIO videos don't have thumbnails
          platform: 'cached'
        }
        
        setCurrentVideo(video)
        
        // Analyze video for segments (transcript included in response)
        await analyzeVideo(video.id, videoData.url, video.duration)
        return
      }
      
      if (isCloudVideo && videoData.cloudSource) {
        // Cloud storage import — load video metadata and analyze
        const cs = videoData.cloudSource
        const video: Video = {
          id: Date.now().toString(),
          url: videoData.url,
          title: cs.name?.replace(/\.[^.]+$/, '') || videoData.title,
          duration: videoData.duration || 0,
          platform: 'cloud',
        }
        setCurrentVideo(video)
        
        // Analyze with the cloud source URL
        // The analyze endpoint will handle downloading from cloud storage
        await analyzeVideo(video.id, videoData.url, video.duration)
        return
      }
      
      if (isUploadedVideo) {
        // Direct file upload — already saved by /studio/import/upload
        const video: Video = {
          id: Date.now().toString(),
          url: videoData.url,
          title: videoData.title,
          duration: videoData.duration || 0,
          platform: 'upload',
        }
        setCurrentVideo(video)
        await analyzeVideo(video.id, videoData.url, video.duration)
        return
      }

      // If we already have metadata (e.g. from YouTube channel picker), skip the metadata fetch
      if (videoData.platform && videoData.title && videoData.title !== `Video from ${videoData.platform}`) {
        const video: Video = {
          id: Date.now().toString(),
          url: videoData.url,
          title: videoData.title,
          duration: videoData.duration,
          thumbnail: videoData.thumbnail || '',
          platform: videoData.platform,
        }
        setCurrentVideo(video)
        await analyzeVideo(video.id, videoData.url, video.duration)
        return
      }
      
      // Import video metadata from URL (YouTube, etc)
      const response = await api.post('/studio/import/metadata', {
        url: videoData.url
      })
      
      if (response.data.success) {
        const video: Video = {
          id: Date.now().toString(),
          url: videoData.url,
          title: response.data.video.title,
          duration: response.data.video.duration,
          thumbnail: response.data.video.thumbnail,
          platform: response.data.video.platform
        }
        
        setCurrentVideo(video)
        
        // Analyze video for segments and transcript
        await analyzeVideo(video.id, videoData.url, video.duration)
      }
    } catch (error) {
      console.error('Failed to import video:', error)
      alert('Failed to import video. Please try again.')
    } finally {
      setIsLoading(false)
      setProcessingMessage('')
    }
  }

  // Handle loading sample data (pre-analyzed showcase videos)
  const handleSampleLoad = (sampleData: SampleData) => {
    console.log('Loading sample:', sampleData.video.title)
    
    setIsLoading(true)
    setProcessingMessage('Loading sample video...')
    setErrorMessage('')
    
    // Set all data
    setCurrentVideo(sampleData.video)
    setTranscript(sampleData.transcript)
    setSegments(sampleData.segments)
    setSelectedSegments([])
    setSelectedSentences([])
    setCurrentTime(0)
    setIsPlaying(false)
    setIsLoading(false)
    setProcessingMessage('')
    
    console.log(`Loaded sample with ${sampleData.segments.length} segments and ${sampleData.transcript.sentences.length} sentences`)
  }

  // ── Load a saved project from the library ───────────────────
  const loadSavedProject = async (id: string) => {
    setIsLoading(true)
    setProcessingMessage('Loading saved project...')
    setErrorMessage('')
    try {
      const result = await projectApi.getProject(id)
      if (!result.success || !result.project) throw new Error('Project not found')

      const p = result.project
      const video: Video = {
        id: p.id,
        url: p.video_url || p.video_path,
        title: p.title,
        duration: p.video_duration,
        thumbnail: p.thumbnail_url,
        platform: p.video_source,
      }
      setCurrentVideo(video)

      // Restore pipeline state from metadata
      const meta = p.metadata || {}
      if (meta.analyze_job_id) setAnalyzeJobId(meta.analyze_job_id)
      if (meta.pipeline_video_url) setPipelineVideoUrl(meta.pipeline_video_url)

      // Restore segments
      if (p.segments?.length > 0) {
        const restoredSegments: Segment[] = p.segments.map((s: any) => ({
          id: s.id,
          video_id: p.id,
          start: s.start_time,
          end: s.end_time,
          text: s.transcript || s.title || '',
          highlight_score: s.highlight_score,
          tags: s.tags,
          padding: { before: s.padding_before || 0, after: s.padding_after || 0 },
          speaker: s.metadata?.speaker,
          color: s.metadata?.color,
          is_manual: s.metadata?.is_manual,
          confidence: s.highlight_score,
          created_at: s.created_at,
        }))
        setSegments(restoredSegments)

        const selected = p.segments.filter((s: any) => s.selected).map((s: any) => s.id)
        setSelectedSegments(selected)

        setProcessingMessage(`Loaded ${restoredSegments.length} clips from saved project!`)
      }
    } catch (error: any) {
      setErrorMessage(`Failed to load project: ${error.message}`)
    } finally {
      setIsLoading(false)
      setProcessingMessage('')
    }
  }

  // Analyze video to get segments AND transcript
  const analyzeVideo = async (videoId: string, videoUrl?: string, videoDuration?: number) => {
    setIsLoading(true)
    setIsTranscribing(true)
    setProcessingMessage('Starting analysis...')
    setErrorMessage('')
    try {
      // Determine the correct URL — use explicit param, fallback to state
      const effectiveUrl = videoUrl || currentVideo?.url || ''
      const isMinio = effectiveUrl.startsWith('minio://')

      // Step 1: Kick off analysis (returns immediately with job_id)
      const analyzePayload = {
        url: effectiveUrl,
        detection_mode: 'enhanced',
        duration: videoDuration || currentVideo?.duration || 0,
        settings: {
          max_segments: 20,
          min_segment_duration: 15,
          max_segment_duration: 90,
        },
        minio_path: isMinio ? effectiveUrl : undefined
      }

      let response: any
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          response = await api.post('/studio/analyze', analyzePayload, { timeout: 30000 })
          break // success
        } catch (retryErr: any) {
          const status = retryErr?.response?.status
          if ((status === 502 || status === 503 || status === 504) && attempt < 2) {
            console.warn(`Analyze attempt ${attempt + 1} got ${status}, retrying...`)
            setProcessingMessage(`Server busy, retrying (${attempt + 2}/3)...`)
            await new Promise(r => setTimeout(r, 2000 * (attempt + 1)))
            continue
          }
          throw retryErr
        }
      }
      
      if (!response.data.success || !response.data.analyze_job_id) {
        throw new Error(response.data.error || 'Failed to start analysis')
      }
      
      const analyzeId = response.data.analyze_job_id
      setAnalyzeJobId(analyzeId)
      if (response.data.video_url) {
        setPipelineVideoUrl(response.data.video_url)
      }
      if (response.data.video?.duration) {
        setCurrentVideo(prev => prev ? { ...prev, duration: response.data.video.duration } : prev)
      }
      
      // Step 2: Poll for completion
      const POLL_INTERVAL = 2000 // 2s
      const MAX_POLLS = 180 // 6 min max
      
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL))
        
        const statusResp = await api.get(`/studio/analyze/status/${analyzeId}`, { timeout: 15000 })
        const data = statusResp.data
        console.log(`[analyze poll] status=${data.status}`, data._debug || data)
        
        if (data.status === 'downloading') {
          setProcessingMessage('Downloading video...')
        } else if (data.status === 'transcribing') {
          setProcessingMessage(data.message || 'Transcribing audio...')
        } else if (data.status === 'completed') {
          // Done — set all state
          if (data.segments?.length > 0) {
            setSegments(data.segments)
            const method = data.analysis_method === 'llm' ? 'AI highlights' : 'time-based segments'
            setProcessingMessage(`Found ${data.segments.length} ${method}!`)
          }
          
          // Show backend warning (e.g. "use Chat to find highlights") or fallback
          if (data.warning) {
            setErrorMessage(data.warning)
          } else if (!data.segments?.length && !data.transcript?.sentences?.length) {
            const dbg = data._debug ? ` [method=${data._debug.analysis_method}, dur=${data._debug.video_duration}]` : ''
            setErrorMessage(`No segments or transcript found.${dbg}`)
          }
          
          if (data.transcript?.sentences?.length > 0) {
            setTranscript({
              video_id: videoId,
              language: data.transcript.language || 'en',
              words: data.transcript.words || [],
              sentences: data.transcript.sentences || [],
              duration: data.transcript.duration || data.video?.duration || videoDuration || 0,
              created_at: new Date().toISOString()
            })
          }
          
          if (data.video?.duration) {
            setCurrentVideo(prev => prev ? { ...prev, duration: data.video.duration } : prev)
          }
          if (data.video?.title) {
            setCurrentVideo(prev => prev ? { ...prev, title: data.video.title } : prev)
          }
          
          return // Success!
        } else if (data.status === 'failed') {
          throw new Error(data.error || 'Analysis pipeline failed')
        }
      }
      
      // If we get here, we timed out
      setErrorMessage('Analysis timed out. Please try again.')
      
    } catch (error: any) {
      const errMsg = error?.response?.data?.error || error?.message || 'Unknown error'
      console.error('Failed to analyze video:', errMsg)
      setErrorMessage(`Analysis failed: ${errMsg}`)
    } finally {
      setIsLoading(false)
      setIsTranscribing(false)
      setProcessingMessage('')
    }
  }

  // Handle segment selection
  const handleSegmentSelect = (segmentId: string, multiSelect = false) => {
    if (multiSelect) {
      setSelectedSegments(prev => 
        prev.includes(segmentId) 
          ? prev.filter(id => id !== segmentId)
          : [...prev, segmentId]
      )
    } else {
      setSelectedSegments([segmentId])
    }
  }

  // Handle segment update
  const handleSegmentUpdate = async (segmentId: string, updates: Partial<Segment>) => {
    // Update locally immediately for responsiveness
    setSegments(prev => prev.map(seg => 
      seg.id === segmentId ? { ...seg, ...updates } : seg
    ))
    
    // Try to persist to API
    try {
      await api.put(`/studio/segments/${segmentId}`, updates)
    } catch (error) {
      console.error('Failed to persist segment update:', error)
      // Keep local update even if API fails
    }
  }

  // Handle segment deletion
  const handleSegmentDelete = (segmentId: string) => {
    setSegments(prev => prev.filter(seg => seg.id !== segmentId))
    setSelectedSegments(prev => prev.filter(id => id !== segmentId))
  }

  // Handle segment creation
  const handleSegmentCreate = (start: number, end: number) => {
    const newSegment: Segment = {
      id: `segment-${Date.now()}`,
      video_id: currentVideo?.id || '',
      start,
      end,
      text: '',
      is_manual: true,
      confidence: 1.0,
      padding: { before: 0, after: 0 }
    }
    setSegments(prev => [...prev, newSegment].sort((a, b) => a.start - b.start))
    setSelectedSegments([newSegment.id])
  }

  // Apply chat v2 ops (add/adjust/remove/sfx) to the timeline — segment ids round-trip
  const handleApplyChatOps = (ops: ChatOp[]) => {
    // Sound ops are applied OUTSIDE the setSegments updater below: they touch a
    // different piece of state (sfxClips), and calling one state setter inside
    // another's updater is a React anti-pattern that drops writes under batching.
    // The server has already resolved each one to a concrete start time and a
    // same-origin url, so there is nothing to look up here.
    ops.filter(op => op.op === 'sfx').forEach(op => {
      if (op.url && typeof op.start === 'number') {
        addSfxClip({ url: op.url, text: op.name || op.slug || 'SFX', duration: op.duration }, op.start)
      }
    })

    // Sound removal. The server has already turned whatever the model said ("the
    // uplifter", "the sfx on clip 3") into concrete clip ids from the sounds this
    // client sent, so there is nothing to match here — and no way for a vague
    // phrase to delete more than the server decided it meant.
    //
    // Routed through removeClip so the assistant and the timeline's own Remove
    // button take exactly the same path; a second deletion path would be a second
    // thing to keep correct.
    ops.filter(op => op.op === 'remove_sound').forEach(op => {
      const kind = op.sound_kind
      ;(op.sound_ids || []).forEach(id => {
        if (kind) {
          removeClip(kind, id)
        } else {
          // No kind given: the id is unique across tracks anyway, so try each.
          ;(['voice', 'music', 'sfx'] as const).forEach(k => removeClip(k, id))
        }
      })
    })

    setSegments(prev => {
      let next = [...prev]
      ops.forEach((op, i) => {
        if (op.op === 'add' && op.start !== undefined && op.end !== undefined) {
          const newSegment: Segment = {
            id: `segment-${Date.now()}-${i}`,
            video_id: currentVideo?.id || '',
            start: op.start,
            end: op.end,
            text: op.title || op.description || '',
            highlight_score: op.score,
            is_manual: false,
            padding: { before: 0, after: 0 },
          }
          next.push(newSegment)
        } else if (op.op === 'adjust' && op.id) {
          next = next.map(seg =>
            seg.id === op.id
              ? { ...seg, start: op.start ?? seg.start, end: op.end ?? seg.end }
              : seg
          )
        } else if (op.op === 'remove' && op.id) {
          next = next.filter(seg => seg.id !== op.id)
        }
      })
      return next.sort((a, b) => a.start - b.start)
    })

    // Drop selections that point at removed segments
    const removedIds = ops.filter(o => o.op === 'remove' && o.id).map(o => o.id as string)
    if (removedIds.length > 0) {
      setSelectedSegments(prev => prev.filter(id => !removedIds.includes(id)))
    }
  }

  // Handle sentence selection for transcript
  const handleSentenceSelect = (sentenceId: string, multiSelect: boolean) => {
    if (multiSelect) {
      setSelectedSentences(prev => 
        prev.includes(sentenceId) 
          ? prev.filter(id => id !== sentenceId)
          : [...prev, sentenceId]
      )
    } else {
      setSelectedSentences(prev => 
        prev.includes(sentenceId) ? [] : [sentenceId]
      )
    }
  }

  // Create segment from selected sentences
  const handleCreateSegmentFromSentences = (sentenceIds: string[]) => {
    if (!transcript || sentenceIds.length === 0) return
    
    const selectedSentenceObjs = transcript.sentences.filter(s => sentenceIds.includes(s.id))
    if (selectedSentenceObjs.length === 0) return
    
    const start = Math.min(...selectedSentenceObjs.map(s => s.start))
    const end = Math.max(...selectedSentenceObjs.map(s => s.end))
    const text = selectedSentenceObjs.map(s => s.text).join(' ')
    
    const newSegment: Segment = {
      id: `segment-${Date.now()}`,
      video_id: currentVideo?.id || '',
      start,
      end,
      text,
      is_manual: true,
      confidence: 1.0,
      sentences: selectedSentenceObjs,
      padding: { before: 0, after: 0 }
    }
    
    setSegments(prev => [...prev, newSegment].sort((a, b) => a.start - b.start))
    setSelectedSegments([newSegment.id])
    setSelectedSentences([]) // Clear selection
  }

  // Handle clip export — open the ExportPanel instead of inline alert-based flow.
  // Nothing selected used to make this button do nothing at all, with no hint why. Now it takes
  // every highlight, which is what someone pressing Export on a fresh analysis means.
  const handleExportClips = () => {
    if (selectedSegments.length === 0) {
      if (segments.length === 0) {
        setErrorMessage('There is nothing to export yet: analyze the video or mark a highlight first.')
        return
      }
      setSelectedSegments(segments.map(s => s.id))
    }
    if (IS_DEMO) { setShowDemoExport(true); return }
    setShowExportPanel(true)
  }

  // "Play clip" from the highlights list: jump to the moment and stop at its end.
  const playHighlight = (seg: Segment) => {
    setCurrentTime(seg.start)
    setPlayUntil(seg.end)
    setIsPlaying(true)
  }
  const toggleHighlight = (id: string) => handleSegmentSelect(id, true)
  const selectAllHighlights = () => setSelectedSegments(segments.map(s => s.id))

  // Quick export with current defaults
  const handleQuickExport = () => {
    if (selectedSegments.length === 0) return
    setShowExportPanel(true)
  }

  // The demo never shows the import page: it would fire account-only API calls for a visitor
  // with no session.
  if (!currentVideo && IS_DEMO) {
    return (
      <div className="min-h-screen bg-[#0b0d12] text-white flex items-center justify-center p-6 text-center">
        {errorMessage ? (
          <div className="max-w-sm">
            <p className="text-sm text-gray-300">{errorMessage}</p>
            <a href={DEMO_HOME_URL} className="mt-3 inline-block text-sm text-indigo-300 hover:text-white">Go to legit2clip</a>
          </div>
        ) : (
          <div>
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500 mx-auto mb-3" />
            <p className="text-sm text-gray-400">Opening the demo…</p>
          </div>
        )}
      </div>
    )
  }

  if (!currentVideo) {
    return (
      <div className="min-h-screen bg-[#0b0d12] text-white relative flex flex-col">
        {/* Full-screen processing overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="text-center">
              <div className="animate-spin rounded-full h-14 w-14 border-t-2 border-b-2 border-indigo-500 mx-auto mb-4"></div>
              <p className="text-white font-semibold text-lg">{processingMessage || 'Processing...'}</p>
              <p className="text-gray-400 text-sm mt-2">This may take a moment</p>
            </div>
          </div>
        )}

        <header className="h-14 shrink-0 flex items-center justify-between px-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            {/* Back to the legit2cloud workspace. The Studio is now a top-level page
              rather than an iframe, so it owns the whole window and there is no
              surrounding nav to escape through — without this the only way out is
              the browser back button. /app is safe to hard-code: every real entry
              to this SPA arrives via legit2cloud's /api/l2c-sso handshake (the
              clipper GUI does not link here), so the workspace always exists. If
              the SPA is ever served standalone, this needs a host-supplied home
              URL instead. */}
            {!STUDIO_EMBEDDED && (<>
            <a href="/app" title="Back to your workspace"
              className="text-sm text-slate-400 hover:text-white whitespace-nowrap">← Workspace</a>
            <div className="h-5 w-px bg-white/10" />
            </>)}
            <div className="flex items-center gap-2">
              <img src={`${import.meta.env.BASE_URL}brand/logo-64.png`} alt="" aria-hidden="true" className="w-6 h-6 shrink-0" />
              <span className="font-semibold">Studio</span>
            </div>
          </div>
          <button onClick={() => setShowProjectLibrary(true)} className="text-sm text-slate-400 hover:text-white">📁 My Projects</button>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="w-full max-w-3xl">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-2xl shadow-2xl">🎬</div>
              <h1 className="text-4xl font-bold tracking-tight">Create your next short</h1>
              <p className="mt-2 text-slate-400">Drop in any video and turn it into captioned clips — with AI voice, music, and your style, all rendered on our GPU cloud.</p>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <SampleSelector onSampleLoad={handleSampleLoad} isLoading={isLoading} />
              <span className="text-sm text-slate-500">— try a sample, or import your own below</span>
            </div>

            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="mb-3 text-center text-sm font-medium text-slate-300">Import your own video</div>
              <ImportVideo onImport={handleVideoImport} isLoading={isLoading} />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[['✂️', 'Auto-clip highlights'], ['💬', 'Captions & transcript'], ['🎙', 'Voice & narration'], ['🎵', 'Music & SFX']].map(([ic, lbl]) => (
                <div key={lbl} className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-3 text-center">
                  <div className="text-lg">{ic}</div>
                  <div className="mt-1 text-[11px] text-slate-400">{lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </main>

        {/* Project Library on landing page */}
        {showProjectLibrary && (
          <ProjectLibrary
            onLoadProject={loadSavedProject}
            onNewProject={() => setShowProjectLibrary(false)}
            onClose={() => setShowProjectLibrary(false)}
          />
        )}
      </div>
    )
  }

  // ── reskin: derived helpers ──────────────────────────────────
  const ASPECT_CLASS = { '9:16': 'aspect-[9/16]', '1:1': 'aspect-square', '16:9': 'aspect-video' } as const
  const currentSentence = transcript?.sentences?.find(s => currentTime >= s.start && currentTime <= s.end)
  const mediaTiles = segments.slice(0, 5)
  const runCommand = (text: string) => {
    const t = (text || '').trim()
    if (!t) return
    setChatSeed(t)
    setRightTab('assistant')
    setCommandText('')
  }
  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`
  const QUICK_ACTIONS: { label: string; prompt: string }[] = [
    { label: 'Auto-caption', prompt: 'Add captions to the whole video.' },
    { label: 'Remove filler words', prompt: 'Remove filler words and long silences.' },
    { label: 'Find the best 30s', prompt: 'Find the best 30 seconds for a short.' },
    { label: 'Generate a hook', prompt: 'Write a punchy hook for the opening line.' },
    { label: 'Add B-roll', prompt: 'Suggest B-roll ideas for each section.' },
  ]
  const CREATE_ITEMS: { key: 'video' | 'image' | 'music' | 'voice' | 'sfx' | 'library'; name: string; sub: string; icon: string; color: string }[] = [
    { key: 'video', name: 'Video', sub: 'image or prompt → clip', icon: '▶', color: 'from-rose-500 to-orange-500' },
    { key: 'image', name: 'Image', sub: 'prompt → visual / B-roll', icon: '🖼', color: 'from-amber-500 to-yellow-500' },
    { key: 'music', name: 'Music', sub: 'prompt → original track', icon: '♪', color: 'from-fuchsia-500 to-purple-500' },
    { key: 'voice', name: 'Voiceover', sub: 'script → narration', icon: '🎙', color: 'from-emerald-500 to-teal-500' },
    { key: 'sfx', name: 'Sound FX', sub: 'describe → effect', icon: '✦', color: 'from-sky-500 to-indigo-500' },
    // The user's OWN sounds. Last in the rail because the others CREATE something;
    // this one is what they already have, and it is what the assistant means when
    // they say "my airhorn".
    { key: 'library', name: 'My Library', sub: 'your sounds & beds', icon: '≡', color: 'from-slate-400 to-slate-600' },
  ]

  return (
    <div className="h-screen bg-[#0b0d12] text-white flex flex-col overflow-hidden">
      {/* ── Top bar ── */}
      <header className="h-14 shrink-0 flex items-center justify-between px-3 md:px-4 border-b border-white/10 bg-[#0e1017]">
        <div className="flex items-center gap-3 min-w-0">
          {/* Drawer trigger, phones only — the left rail has no column to live in there. */}
          <button onClick={() => setMobileDrawer(d => (d === 'create' ? 'none' : 'create'))}
            className="md:hidden -ml-1 w-9 h-9 shrink-0 rounded-lg flex items-center justify-center text-gray-300 hover:bg-white/10"
            aria-label="Create with AI">
            <Menu size={18} />
          </button>
{/* Back to the legit2cloud workspace. The Studio is now a top-level page
              rather than an iframe, so it owns the whole window and there is no
              surrounding nav to escape through — without this the only way out is
              the browser back button. /app is safe to hard-code: every real entry
              to this SPA arrives via legit2cloud's /api/l2c-sso handshake (the
              clipper GUI does not link here), so the workspace always exists. If
              the SPA is ever served standalone, this needs a host-supplied home
              URL instead. The public demo is exactly that case: it has no
              workspace, so it links home to legit2clip. */}
            {IS_DEMO ? (<>
            <a href={DEMO_HOME_URL} title="legit2clip home"
              className="hidden sm:inline text-sm text-slate-400 hover:text-white whitespace-nowrap">← legit2clip</a>
            <div className="hidden sm:block h-5 w-px bg-white/10" />
            </>) : !STUDIO_EMBEDDED && (<>
            <a href="/app" title="Back to your workspace"
              className="text-sm text-slate-400 hover:text-white whitespace-nowrap">← Workspace</a>
            <div className="h-5 w-px bg-white/10" />
            </>)}
            <div className="flex items-center gap-2">
            <img src={`${import.meta.env.BASE_URL}brand/logo-64.png`} alt="" aria-hidden="true" className="w-6 h-6 shrink-0" />
            <span className="font-semibold">Studio</span>
          </div>
          <div className="h-5 w-px bg-white/10 hidden sm:block" />
          <div className="hidden sm:flex items-center gap-2 min-w-0 text-sm">
            <span className="text-gray-500 shrink-0">Project ·</span>
            <span className="text-gray-200 truncate max-w-[140px] lg:max-w-[240px]">{currentVideo.title}</span>
            {/* Only claim "saved" once autosave has actually created the project. */}
            {projectId && !IS_DEMO && (
              <span className="shrink-0 px-1.5 py-0.5 text-[10px] rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">saved</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 md:gap-2">
          <div className="flex items-center bg-white/5 rounded-lg p-0.5 text-xs">
            {(['9:16', '1:1', '16:9'] as const).map(a => (
              <button key={a} onClick={() => setAspect(a)}
                className={`px-2 py-1 rounded-md transition-colors ${aspect === a ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'}`}>{a}</button>
            ))}
          </div>
          <span className="hidden lg:inline text-[11px] font-mono text-gray-500 px-2 py-1 rounded bg-white/5">rendered on a GPU cloud</span>
          {!IS_DEMO && <DesktopPromo />}
          <button onClick={() => setIsPlaying(p => !p)} className="hidden sm:inline-flex px-3 py-1.5 text-sm rounded-lg border border-white/15 text-gray-200 hover:bg-white/5">Preview</button>
          <button onClick={handleExportClips} className="px-2.5 md:px-3 py-1.5 text-sm rounded-lg bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white font-medium hover:opacity-90 whitespace-nowrap">
            Export<span className="hidden sm:inline">{selectedSegments.length > 1 ? ` ${selectedSegments.length} shorts` : ' short'} →</span>
          </button>
          {/* Right-hand panels — transcript, captions, the assistant — reachable on a
              phone, where they have no column of their own. */}
          <button onClick={() => setMobileDrawer(d => (d === 'panels' ? 'none' : 'panels'))}
            className="md:hidden w-9 h-9 shrink-0 rounded-lg flex items-center justify-center text-gray-300 hover:bg-white/10"
            aria-label="Transcript and assistant">
            <PanelRight size={18} />
          </button>
          {!IS_DEMO && (
            <button onClick={() => setShowSettings(true)} className="w-8 h-8 shrink-0 rounded-full bg-white/10 text-xs font-semibold flex items-center justify-center hover:bg-white/20" title="Settings">JS</button>
          )}
        </div>
      </header>
      {IS_DEMO && <DemoBanner original={currentVideo.original} />}

      {/* ── Body ── */}
      <div className="flex-1 flex min-h-0 relative">
        {/* Drawer backdrop. Phones only; tapping it closes whichever rail is open. */}
        {mobileDrawer !== 'none' && (
          <div className="md:hidden fixed inset-0 top-14 z-30 bg-black/60"
               onClick={() => setMobileDrawer('none')} aria-hidden="true" />
        )}

        {/* Left rail — a column from md up, a slide-over below it. */}
        <aside className={`w-60 shrink-0 border-r border-white/10 bg-[#0e1017] flex flex-col overflow-y-auto
                           max-md:fixed max-md:inset-y-0 max-md:top-14 max-md:z-40 max-md:w-[17rem]
                           max-md:transition-transform max-md:duration-200
                           ${mobileDrawer === 'create' ? 'max-md:translate-x-0' : 'max-md:-translate-x-full'}`}>
          <div className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Create with AI</div>
            <div className="space-y-1">
              {CREATE_ITEMS.map(it => (
                <button key={it.key} onClick={() => { setActiveCreate(it.key); setMobileDrawer('none') }}
                  className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-white/5 text-left transition-colors">
                  <span className={`w-8 h-8 rounded-lg bg-gradient-to-br ${it.color} flex items-center justify-center text-sm shrink-0`}>{it.icon}</span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium leading-tight">{it.name}</span>
                    <span className="block text-[11px] text-gray-500 truncate">{it.sub}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="p-3 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Media</div>
            <div className="grid grid-cols-2 gap-2">
              {mediaTiles.map(seg => (
                <button key={seg.id} onClick={() => { setCurrentTime(seg.start); handleSegmentSelect(seg.id); setMobileDrawer('none') }}
                  className="relative aspect-square rounded-lg bg-gradient-to-br from-indigo-900/40 to-fuchsia-900/30 border border-white/10 overflow-hidden hover:border-white/25 transition-colors">
                  <span className="absolute bottom-1 left-1 text-[10px] font-mono text-gray-200 bg-black/40 px-1 rounded">{fmt(seg.start)}</span>
                </button>
              ))}
              {!IS_DEMO && (
                <button onClick={() => setShowChannelPicker(true)}
                  className="aspect-square rounded-lg border border-dashed border-white/15 text-gray-500 hover:text-white hover:border-white/30 flex items-center justify-center text-xl">+</button>
              )}
            </div>
          </div>
        </aside>

        {/* Center */}
        <main className="flex-1 min-w-0 flex flex-col">
          {/* Demo on a phone: the video full width and the highlights under it. The editing
              timeline and command bar are hidden there; they squeezed the video to a thumbnail. */}
          <div className={`flex-1 min-h-0 flex items-center justify-center p-4 ${IS_DEMO ? 'max-md:flex-none max-md:p-3' : ''}`}>
            <div className={`relative h-full ${ASPECT_CLASS[aspect]} max-h-full rounded-2xl overflow-hidden border border-white/10 bg-black shadow-2xl ${IS_DEMO ? 'max-md:w-full max-md:h-auto' : ''}`}>
              <VideoPlayer
                video={currentVideo}
                currentTime={currentTime}
                isPlaying={isPlaying}
                onTimeUpdate={setCurrentTime}
                onPlayStateChange={setIsPlaying}
                onDurationChange={(dur) => {
                  if (dur > 0 && (!currentVideo.duration || currentVideo.duration === 0)) {
                    setCurrentVideo(prev => prev ? { ...prev, duration: dur } : prev)
                  }
                }}
              />
              {/* Layered audio (music / voice / SFX) mixed over the base video during preview */}
              <AudioMixer
                clips={[...voiceClips, ...musicClips, ...sfxClips]}
                currentTime={currentTime}
                isPlaying={isPlaying}
              />
              {(isLoading || isTranscribing) && (
                <div className="absolute top-3 left-3 flex items-center gap-1.5 text-[11px] bg-black/60 backdrop-blur px-2 py-1 rounded-full border border-white/10">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {processingMessage || 'working…'}
                </div>
              )}
              {currentSentence && !isLoading && (
                <div className="absolute inset-x-0 bottom-16 md:bottom-10 flex justify-center px-4 md:px-6 pointer-events-none">
                  <span className="text-center text-sm md:text-lg font-extrabold leading-snug text-white [text-shadow:0_2px_6px_rgba(0,0,0,0.85)] line-clamp-3">{currentSentence.text}</span>
                </div>
              )}
            </div>
          </div>

          {IS_DEMO && (
            <div className="md:hidden flex-1 min-h-0 flex flex-col border-t border-white/10">
              <HighlightsPanel
                segments={segments}
                transcript={transcript}
                selectedSegments={selectedSegments}
                currentTime={currentTime}
                onToggleSelect={toggleHighlight}
                onSelectAll={selectAllHighlights}
                onPlay={playHighlight}
                onExport={handleExportClips}
              />
            </div>
          )}

          <div className={`shrink-0 px-4 pb-3 space-y-2 ${IS_DEMO ? 'max-md:hidden' : ''}`}>
            {errorMessage && !isLoading && (
              <div className="text-xs px-3 py-2 rounded-lg bg-amber-900/40 border border-amber-600/40 text-amber-200 flex items-center justify-between">
                <span>{errorMessage}</span>
                <button onClick={() => setErrorMessage('')} className="ml-3 text-amber-400 hover:text-white">&times;</button>
              </div>
            )}
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2">
              <span className="text-indigo-400">✦</span>
              <input
                value={commandText}
                onChange={e => setCommandText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') runCommand(commandText) }}
                placeholder="Tell the studio what to do — e.g. cut the silences, add captions, and drop a soft music bed"
                className="flex-1 bg-transparent text-sm placeholder-gray-500 focus:outline-none"
              />
              <button onClick={() => runCommand(commandText)} disabled={!commandText.trim()}
                className="px-3 py-1 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed">Run</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_ACTIONS.map(qa => (
                <button key={qa.label} onClick={() => runCommand(qa.prompt)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                  <span className="text-indigo-400">✦</span> {qa.label}
                </button>
              ))}
            </div>
          </div>
        </main>

        {/* Right panel — same treatment, from the other edge. */}
        <aside className={`w-80 shrink-0 border-l border-white/10 bg-[#0e1017] flex flex-col min-h-0
                           max-md:fixed max-md:inset-y-0 max-md:right-0 max-md:top-14 max-md:z-40
                           max-md:w-[min(22rem,88vw)] max-md:transition-transform max-md:duration-200
                           ${mobileDrawer === 'panels' ? 'max-md:translate-x-0' : 'max-md:translate-x-full'}`}>
          <div className="flex items-center gap-0.5 p-2 border-b border-white/10 text-[13px]">
            {(['highlights', 'transcript', 'captions', 'assistant'] as const).map(t => (
              <button key={t} onClick={() => setRightTab(t)}
                className={`px-2 py-1.5 rounded-lg capitalize transition-colors whitespace-nowrap ${rightTab === t ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}>{t}</button>
            ))}
          </div>
          <div className="flex-1 min-h-0 flex flex-col">
            {rightTab === 'highlights' && (
              <HighlightsPanel
                segments={segments}
                transcript={transcript}
                selectedSegments={selectedSegments}
                currentTime={currentTime}
                onToggleSelect={toggleHighlight}
                onSelectAll={selectAllHighlights}
                onPlay={(seg) => { playHighlight(seg); setMobileDrawer('none') }}
                onExport={handleExportClips}
              />
            )}
            {rightTab === 'transcript' && (
              <TranscriptPanel
                transcript={transcript}
                currentTime={currentTime}
                segments={segments}
                selectedSentences={selectedSentences}
                onSentenceSelect={handleSentenceSelect}
                onCreateSegmentFromSentences={handleCreateSegmentFromSentences}
                onSeek={setCurrentTime}
                isLoading={isTranscribing}
              />
            )}
            {rightTab === 'captions' && (
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {transcript?.sentences?.length ? transcript.sentences.map(s => (
                  <button key={s.id} onClick={() => setCurrentTime(s.start)}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${currentTime >= s.start && currentTime <= s.end ? 'bg-indigo-600/20 border-indigo-500/40' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
                    <span className="text-[10px] font-mono text-gray-500 block mb-0.5">{fmt(s.start)}</span>
                    <span className="text-gray-200">{s.text}</span>
                  </button>
                )) : <div className="text-center text-gray-500 text-sm py-10">Captions appear here once the video is analyzed.</div>}
              </div>
            )}
            {IS_DEMO && rightTab === 'assistant' && <DemoGate what="The assistant" />}
            {!IS_DEMO && <div className={rightTab === 'assistant' ? 'flex-1 min-h-0 flex flex-col' : 'hidden'}>
              <ChatPanel
                sessionId={currentVideo?.id}
                transcript={transcript}
                videoDuration={currentVideo?.duration || 0}
                segments={segments}
                // Everything audible on the timeline, so the assistant can be asked
                // to take a sound away and actually has the ids to do it.
                sounds={[
                  ...voiceClips.map(c => ({ id: c.id, kind: 'voice' as const, name: c.text, start: c.start, duration: c.duration })),
                  ...musicClips.map(c => ({ id: c.id, kind: 'music' as const, name: c.text, start: c.start, duration: c.duration })),
                  ...sfxClips.map(c => ({ id: c.id, kind: 'sfx' as const, name: c.text, start: c.start, duration: c.duration })),
                ]}
                onAddSegments={(newSegs) => {
                  setSegments(newSegs.map(s => ({ ...s, video_id: currentVideo?.id || s.video_id })))
                }}
                onApplyOps={handleApplyChatOps}
                onSeek={setCurrentTime}
                seedMessage={chatSeed}
                onSeedConsumed={() => setChatSeed(null)}
              />
            </div>}
          </div>
        </aside>
      </div>

      {/* ── Bottom timeline ── */}
      <div className={`shrink-0 border-t border-white/10 bg-[#0e1017] px-3 py-2 ${IS_DEMO ? 'max-md:hidden' : ''}`}>
        <div className="flex items-center justify-between mb-1 text-[11px] text-gray-500">
          <span>{segments.length} clips · {fmt(currentVideo.duration)}</span>
          <span>{aspect} · {currentVideo.title}</span>
        </div>
        {selectedAudioClip && (() => {
          const arr: any[] = ({ voice: voiceClips, music: musicClips, sfx: sfxClips, image: imageClips } as any)[selectedAudioClip.kind] || []
          const c = arr.find((x: any) => x.id === selectedAudioClip.id)
          if (!c) return null
          const isAudio = selectedAudioClip.kind !== 'image'
          const icon = selectedAudioClip.kind === 'voice' ? '🎙' : selectedAudioClip.kind === 'music' ? '♪' : selectedAudioClip.kind === 'sfx' ? '🔊' : '🖼'
          return (
            <div className="flex items-center gap-3 mb-1.5 px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-[11px]">
              <span className="text-gray-200 truncate max-w-[200px]">{icon} {c.text}</span>
              <span className="text-gray-500 font-mono">{fmt(c.start)}–{fmt(c.start + c.duration)}</span>
              {isAudio && (
                <label className="flex items-center gap-1.5 text-gray-400">
                  <span>Vol</span>
                  <input type="range" min={-30} max={6} step={1} value={c.gain_db ?? 0} onChange={(e) => updateClip(selectedAudioClip.kind, selectedAudioClip.id, { gain_db: Number(e.target.value) })} className="w-28 accent-indigo-500" />
                  <span className="w-12 font-mono text-gray-300">{c.gain_db ?? 0} dB</span>
                </label>
              )}
              <button onClick={() => removeClip(selectedAudioClip.kind, selectedAudioClip.id)} className="ml-auto text-gray-400 hover:text-red-400">Remove</button>
              <button onClick={() => setSelectedAudioClip(null)} className="text-gray-500 hover:text-white text-sm leading-none">×</button>
            </div>
          )
        })()}
        <Timeline
          segments={segments}
          selectedSegments={selectedSegments}
          currentTime={currentTime}
          duration={currentVideo.duration}
          onSegmentSelect={handleSegmentSelect}
          onSegmentUpdate={handleSegmentUpdate}
          onSeek={setCurrentTime}
          transcript={transcript}
          musicTrack={musicTrack}
          voiceClips={voiceClips}
          onVoiceDrop={addVoiceClip}
          musicClips={musicClips}
          sfxClips={sfxClips}
          onMusicDrop={addMusicClip}
          onSfxDrop={addSfxClip}
          imageClips={imageClips}
          onImageDrop={addImageClip}
          onClipUpdate={updateClip}
          onClipSelect={(k, id) => setSelectedAudioClip({ kind: k as any, id })}
          selectedClip={selectedAudioClip}
          captionStyle={captionStyle}
          onCaptionStyleChange={setCaptionStyle}
          karaokeMode={karaokeMode}
          onKaraokeToggle={() => setKaraokeMode(v => !v)}
        />
      </div>

      {/* ── Create drawer ── */}
      {activeCreate && (
        <div className="fixed inset-0 z-40 flex justify-end pointer-events-none">
          <div className="relative w-[400px] max-w-full h-full bg-[#0e1017] border-l border-white/10 shadow-2xl overflow-y-auto p-3 flex flex-col pointer-events-auto">
            <div className="flex items-center justify-between mb-3 shrink-0">
              <h3 className="text-sm font-semibold">{activeCreate === 'voice' ? 'Voiceover' : activeCreate === 'sfx' ? 'Sound FX' : activeCreate === 'music' ? 'Music' : activeCreate === 'image' ? 'Image' : activeCreate === 'library' ? 'My Library' : 'Video'}</h3>
              <button onClick={() => setActiveCreate(null)} className="text-gray-400 hover:text-white text-lg leading-none">&times;</button>
            </div>
            {IS_DEMO && <DemoGate what={activeCreate === 'voice' ? 'Voiceover' : activeCreate === 'sfx' ? 'Sound FX' : activeCreate === 'music' ? 'Music' : activeCreate === 'image' ? 'Image generation' : activeCreate === 'library' ? 'Your library' : 'Video generation'} />}
            {!IS_DEMO && activeCreate === 'music' && <MusicPanel musicTrack={musicTrack} onUseTrack={setMusicTrack} onClearTrack={() => setMusicTrack(null)} onAddToTimeline={addMusicClip} />}
            {!IS_DEMO && activeCreate === 'voice' && <VoicePanel onAddToTimeline={(c) => addVoiceClip(c)} />}
            {!IS_DEMO && activeCreate === 'sfx' && <SfxPanel onAddToTimeline={addSfxClip} />}
            {!IS_DEMO && activeCreate === 'library' && (
              <LibraryPanel onAddToTimeline={(c) => (c.kind === 'music' ? addMusicClip(c) : addSfxClip(c))} />
            )}
            {!IS_DEMO && activeCreate === 'image' && <ImagePanel onAddToTimeline={addImageClip} />}
            {!IS_DEMO && activeCreate === 'video' && (
              <div className="text-center text-gray-400 text-sm py-12">
                <div className="text-4xl mb-3">▶</div>
                Text-to-video is coming soon on your GPU cloud.
                <p className="text-xs text-gray-500 mt-2">For now, import a video, or generate images for B-roll.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Modal */}
      <StudioSettings isOpen={showSettings} onClose={() => setShowSettings(false)} />

      {showDemoExport && (
        <DemoExportModal count={selectedSegments.length} original={currentVideo.original} onClose={() => setShowDemoExport(false)} />
      )}

      {/* Export Panel */}
      {showExportPanel && currentVideo && (
        <ExportPanel
          video={currentVideo}
          segments={segments}
          selectedSegments={selectedSegments}
          analyzeJobId={analyzeJobId}
          pipelineVideoUrl={pipelineVideoUrl}
          selectedIntro={selectedIntro}
          selectedOutro={selectedOutro}
          musicTrack={musicTrack}
          audioTracks={[
            ...voiceClips.map(c => ({ url: c.url, start: c.start, duration: c.duration, kind: 'voice' as const, gain_db: c.gain_db, offset: c.offset })),
            ...musicClips.map(c => ({ url: c.url, start: c.start, duration: c.duration, kind: 'music' as const, gain_db: c.gain_db, offset: c.offset })),
            ...sfxClips.map(c => ({ url: c.url, start: c.start, duration: c.duration, kind: 'sfx' as const, gain_db: c.gain_db, offset: c.offset })),
          ]}
          imageTracks={imageClips.map(c => ({ url: c.url, start: c.start, duration: c.duration }))}
          captionStyle={captionStyle}
          onCaptionStyleChange={setCaptionStyle}
          karaokeMode={karaokeMode}
          onKaraokeToggle={() => setKaraokeMode(v => !v)}
          onClose={() => setShowExportPanel(false)}
          onExportedBlob={setExportedBlob}
          onExportedUrl={setExportedDownloadUrl}
          projectId={projectId}
          onOpenYouTube={() => setShowYouTubePublish(true)}
        />
      )}

      {/* Project Library */}
      {showProjectLibrary && (
        <ProjectLibrary
          onLoadProject={loadSavedProject}
          onNewProject={() => {
            setCurrentVideo(null)
            setSegments([])
            setSelectedSegments([])
            setSelectedSentences([])
            setTranscript(null)
            setAnalyzeJobId(null)
            setPipelineVideoUrl(null)
            setMusicTrack(null)
            setErrorMessage('')
            setProcessingMessage('')
            setShowExportPanel(false)
          }}
          onClose={() => setShowProjectLibrary(false)}
        />
      )}

      {/* YouTube Publish Modal */}
      {showYouTubePublish && (
        <YouTubePublishPanel
          videoBlob={exportedBlob}
          downloadUrl={exportedDownloadUrl}
          clipTitle={segments.find(s => selectedSegments.includes(s.id))?.text || currentVideo.title}
          originalTitle={currentVideo.title}
          transcriptSnippet={
            transcript?.sentences
              ?.filter(s => {
                const sel = segments.find(seg => selectedSegments.includes(seg.id))
                return sel && s.start >= sel.start && s.end <= sel.end
              })
              ?.map(s => s.text)
              ?.join(' ')
              ?.slice(0, 500) || ''
          }
          transcriptText={
            transcript?.sentences
              ?.map(s => `[${s.start.toFixed(1)}s] ${s.text}`)
              ?.join('\n') || ''
          }
          clipStart={segments.find(s => selectedSegments.includes(s.id))?.start || 0}
          clipEnd={segments.find(s => selectedSegments.includes(s.id))?.end || 0}
          tagsHint={segments
            .filter(s => selectedSegments.includes(s.id))
            .flatMap(s => s.tags || [])
          }
          onClose={() => setShowYouTubePublish(false)}
        />
      )}

      {/* YouTube Channel Video Picker */}
      {showChannelPicker && (
        <YouTubeChannelPicker
          onSelectVideo={(video) => {
            setShowChannelPicker(false)
            handleVideoImport({
              url: video.url,
              title: video.title,
              duration: video.duration,
            })
          }}
          onClose={() => setShowChannelPicker(false)}
        />
      )}
    </div>
  )
}

export default App
